import React, { useEffect, useRef } from 'react'
import { Send } from 'lucide-react'
import { CrmComment } from 'types/crm/comments'
import { ContactOwner } from 'types/crm/contacts'
import { getInitials } from 'utils/getInitials'
import TipTapEditor from 'components/common/RichTextEditor/RichTextEditor'

interface CrmReplyComposerProps {
    replyTarget: CrmComment
    replyText: string
    setReplyText: (text: string) => void
    onCancel: () => void
    onSubmit: () => void
    isSubmitting: boolean
    mentionUsers: ContactOwner[]
    currentUser: { firstName?: string; lastName?: string } | null | undefined
}

const isEmptyRichText = (text: string) =>
    !text ||
    text === '<p></p>' ||
    (text.replace(/<[^>]*>?/gm, '').trim() === '' && !text.includes('<img'))

const CrmReplyComposer: React.FC<CrmReplyComposerProps> = ({
    replyTarget, replyText, setReplyText, onCancel, onSubmit, isSubmitting, mentionUsers, currentUser,
}) => {
    const isEmpty = isEmptyRichText(replyText)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, [])

    return (
        <div ref={containerRef} className="flex gap-2 min-w-0 mt-1">
            <div className="w-7 h-7 bg-primary-subtle rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                {getInitials(currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Y')}
            </div>
            <div className="flex-1 min-w-0">
                <div className="border border-primary-subtle rounded-lg bg-primary-variant overflow-hidden">
                    <TipTapEditor
                        initialContent={replyText}
                        onChange={setReplyText}
                        placeholder={`Reply to ${replyTarget.createdByName || 'comment'}... (Type @ to mention)`}
                        className={isSubmitting ? 'opacity-50 pointer-events-none min-h-[50px]' : 'min-h-[50px]'}
                        mentionUsers={mentionUsers}
                        isComment
                    />
                </div>
                <div className="flex items-center justify-end gap-2 mt-2">
                    <button onClick={onCancel} disabled={isSubmitting} className="px-3 py-1.5 text-xs text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded disabled:opacity-50">
                        Cancel
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={isEmpty || isSubmitting}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${!isEmpty && !isSubmitting ? 'button-active' : 'button-inactive opacity-50 cursor-not-allowed'}`}
                    >
                        {isSubmitting ? 'Posting...' : 'Reply'}
                        {!isSubmitting && <Send className="w-3 h-3" />}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default CrmReplyComposer